
import { Vue } from "vue-class-component";
import moment from "moment";
import { set, get, del, keys } from "idb-keyval";
import { Match, Player } from "../models/Models";
import { Guid } from "guid-typescript";
import router from '../router';


export default class MatchDetails extends Vue {
  public match?: Match = new Match();
  public newPlayerName = "";
  public matchLoaded = false;
  
  public previousMatch: Match = new Match();
  public previousMatchPlayers: string;

  $refs: {
    newPlayerInput: HTMLFormElement;
    opponentInput: HTMLFormElement;
  };

  async created() {
    const id = (this as any).$route.params.id;
    this.match = await get(id);

    if (!this.match) {
      return;
    }

    this.sortPlayers();
    
    // get all matches to see if we can import players from the previous match
    const allMatchKeys = await keys();
    if (allMatchKeys.length > 1) {
      // if we only have one match, that is the current match - no need to try to find
      // the previous match
      let allMatches: Array<Match> = [];

      for (const key of allMatchKeys) {
        allMatches.push(await get(key));
      }

      allMatches = allMatches.sort((a, b) => b.created.localeCompare(a.created));

      for(const otherMatch of allMatches) {
        if (otherMatch.created < this.match.created) {
          this.previousMatch = otherMatch;

          let previousMatchPlayers = '';
          for(let i = 0; i < otherMatch.players.length; i++) {
            previousMatchPlayers += otherMatch.players[i].name;

            if (i + 2 < otherMatch.players.length) {
              previousMatchPlayers += ', ';
            } else if (i + 2 === otherMatch.players.length) {
              previousMatchPlayers += ' og ';              
            }
          }

          this.previousMatchPlayers = previousMatchPlayers;
          
          break;
        }
      }
    }

    if (!this.match) {
      return;
    }

    this.matchLoaded = true;

    if (!this.match.opponent || this.match.opponent == '') {      
      this.$refs.opponentInput.focus();
    } else {
      if (this.match.players.length == 0) {
        this.$refs.newPlayerInput.focus();
      }
    }

    if (this.match?.players.length == 0) {
      this.addPlayer();
    }
  }

  async getPreviousMatchPlayers() {
    if (!this.previousMatch) {      
      return;
    }

    this.previousMatch.players.forEach(p => {    
      const newPlayer: Player = {
        id: p.id,
        key: p.id,
        name: p.name,
        lastSecondsOnField: 0,
        lastSecondsOfField: 0,
        totalSecondsOnField: 0,
        lastOnField: undefined,
        lastOfField: undefined,
        isOnField: false,
        lastStartOnField: undefined,
        _editing: false,
        _editingName: undefined,
        _timeOnField: "00:00",
        _timeOffField: "00:00",
        _timeTotal: "00:00"
      };

      this.match?.players.push(newPlayer);
    });
    
    await this.saveMatch();
  }

  private editPlayer(player: Player) {
    player._editing = true;
    player._editingName = player.name;
  }

  private savePlayer(player: Player) {
    if (player._editingName) {
      player.name = player._editingName;
    }
    player._editing = false;

    this.saveMatch();
  }

  private deletePlayer(player: Player) {
    if (!this.match) {
      return;
    }

    this.match.players = this.match.players.filter(x => x.id != player.id);
    this.saveMatch();
  }

  public async deleteMatch() {
    if (confirm(this.$t('details.confirmDelete'))) {
      if (!this.match) {
        return;
      }
      await del(this.match.id);

      router.push({ name: "Matches"});
    }
  }

  private async addPlayer() {
    if (this.newPlayerName == "") {
      return;
    }

    if (this.match?.players) {
      const idx = Guid.create().toString();

      const newPlayer: Player = {
        id: idx,        
        key: idx,
        name: this.newPlayerName,
        lastSecondsOnField: 0,
        lastSecondsOfField: 0,
        totalSecondsOnField: 0,
        lastOnField: undefined,
        lastOfField: undefined,
        isOnField: false,
        lastStartOnField: undefined,
        _editing: false,
        _editingName: undefined,
        _timeOnField: "00:00",
        _timeOffField: "00:00",
        _timeTotal: "00:00"
      };

      this.match?.players.push(newPlayer);

      await this.saveMatch();

      this.newPlayerName = "";
      this.$refs.newPlayerInput.focus();
    }
  }

  async saveMatch() {
    if (!this.match) {
      return;
    }

    this.sortPlayers();

    const match = JSON.parse(JSON.stringify(this.match));
    await set(match.id, match);
  }

  goToMatch() {
    if (this.match) {
      router.push({ name: "PlayMatch", params: { id: this.match.id } });
    }
  }

  private sortPlayers() {
    if (!this.match) {
      return;
    }

    this.match.players = this.match.players.sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  focusAddPlayer() {
    this.$refs.newPlayerInput.focus();
  }
}
